import { PriceListRoute } from "../hooks/UseFetchPriceListRoutes";

export interface RoutePairGroup {
  routePairCode: string;
  routes: PriceListRoute[];
}

export interface SeaAreaGroup {
  seaAreaName: string;
  routePairs: RoutePairGroup[];
}

const byName = (a: string, b: string) => a.localeCompare(b);

const multiLegRouteType = "M";

/**
 * Multi-leg routes belong to the sea area of whichever leg they happen to
 * start in, which scatters them through the filter. The price list collects
 * them under a sea area of their own instead.
 */
export const multiLegSeaAreaName = "Multileg";

const seaAreaNameOf = (route: PriceListRoute) =>
  route.routeType === multiLegRouteType
    ? multiLegSeaAreaName
    : route.seaAreaName;

const bySeaAreaName = (a: string, b: string) => {
  if (a === multiLegSeaAreaName || b === multiLegSeaAreaName) {
    return a === b ? 0 : a === multiLegSeaAreaName ? 1 : -1;
  }

  return byName(a, b);
};

export const buildRouteTree = (routes: PriceListRoute[]): SeaAreaGroup[] => {
  const seaAreas = new Map<string, Map<string, PriceListRoute[]>>();

  routes.forEach((route) => {
    const seaAreaName = seaAreaNameOf(route);
    const routePairs =
      seaAreas.get(seaAreaName) ?? new Map<string, PriceListRoute[]>();
    seaAreas.set(seaAreaName, routePairs);
    routePairs.set(route.routePairCode, [
      ...(routePairs.get(route.routePairCode) ?? []),
      route,
    ]);
  });

  return [...seaAreas.entries()]
    .sort(([a], [b]) => bySeaAreaName(a, b))
    .map(([seaAreaName, routePairs]) => ({
      seaAreaName,
      routePairs: [...routePairs.entries()]
        .sort(([a], [b]) => byName(a, b))
        .map(([routePairCode, groupedRoutes]) => ({
          routePairCode,
          routes: [...groupedRoutes].sort((a, b) => byName(a.code, b.code)),
        })),
    }));
};

export const routeCodesIn = (routes: PriceListRoute[]) =>
  routes.map((route) => route.code);

export const routeCodesInPair = (routePair: RoutePairGroup) =>
  routeCodesIn(routePair.routes);

export const routeCodesInSeaArea = (seaArea: SeaAreaGroup) =>
  seaArea.routePairs.flatMap(routeCodesInPair);

export const toggleGroup = (
  selected: string[],
  groupCodes: string[]
): string[] => {
  const allSelected = groupCodes.every((code) => selected.includes(code));

  if (allSelected) {
    return selected.filter((code) => !groupCodes.includes(code));
  }

  return [
    ...selected,
    ...groupCodes.filter((code) => !selected.includes(code)),
  ];
};

export const toggleRoute = (selected: string[], code: string): string[] =>
  selected.includes(code)
    ? selected.filter((selectedCode) => selectedCode !== code)
    : [...selected, code];
